import React from "react";
import { motion } from "framer-motion";
import { Plane, Bus, Building2, Car, Compass, ArrowUpRight } from "lucide-react";
import { useLang } from "../../i18n/LanguageProvider";
import { scrollToId } from "./SmoothScroll";

const SERVICE_ICONS = [Plane, Bus, Building2, Car, Compass];

const IMAGES = [
  "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.unsplash.com/photo-1474487548417-781cb71495f3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.pexels.com/photos/258154/pexels-photo-258154.jpeg?auto=compress&cs=tinysrgb&w=1200",
  "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.unsplash.com/photo-1488646953014-85cb44e25828?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
];

const revealUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

export const Services = () => {
  const { t } = useLang();

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-[#F5F1E8] text-[#0B1416] py-28 md:py-40 lg:py-52"
    >
      {/* Section header */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <motion.div
          variants={revealUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-10"
        >
          <div className="lg:col-span-4">
            <div className="overline text-[#0D7A7D]">— {t.services.eyebrow}</div>
          </div>
          <div className="lg:col-span-8">
            <h2
              data-testid="services-title"
              className="font-heading-tight text-5xl md:text-6xl lg:text-7xl leading-[0.95]"
            >
              {t.services.title}
            </h2>
            <p className="mt-6 text-lg font-light max-w-2xl opacity-70">
              {t.services.subtitle}
            </p>
          </div>
        </motion.div>

        {/* Numbered chapters — asymmetric bento grid */}
        <div className="mt-24 md:mt-32 space-y-32 md:space-y-40">
          {t.services.list.map((svc, i) => {
            const Icon = SERVICE_ICONS[i];
            const reverse = i % 2 === 1;
            return (
              <motion.div
                key={svc.num}
                data-testid={`service-chapter-${svc.num}`}
                variants={revealUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.25 }}
                className={`grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-16 items-center ${
                  reverse ? "md:[&>*:first-child]:order-2" : ""
                }`}
              >
                {/* Image side */}
                <div className={`md:col-span-7 ${reverse ? "md:col-start-6" : ""}`}>
                  <div className="clip-frame relative aspect-[4/3] overflow-hidden">
                    <img
                      src={IMAGES[i]}
                      alt={svc.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute top-4 left-4 flag-pill bg-[#0B1416]/70 backdrop-blur-md text-[#F5F1E8] border-[#F5F1E8]/25">
                      {svc.tag}
                    </div>
                  </div>
                </div>

                {/* Text side */}
                <div className={`md:col-span-5 relative ${reverse ? "md:col-start-1 md:row-start-1" : ""}`}>
                  <div
                    aria-hidden
                    className="font-italiana num-outline absolute -top-16 -left-4 select-none pointer-events-none"
                    style={{ fontSize: "clamp(120px, 18vw, 260px)", lineHeight: 1 }}
                  >
                    {svc.num}
                  </div>
                  <div className="relative">
                    <div className="flex items-center gap-3 text-[#0D7A7D]">
                      <Icon size={18} strokeWidth={1.4} />
                      <span className="overline">{t.services.chapter} {svc.num}</span>
                    </div>
                    <h3 className="font-heading-tight text-4xl md:text-5xl lg:text-6xl leading-[1] mt-6">
                      {svc.title}
                    </h3>
                    <p className="mt-6 text-base md:text-lg font-light leading-relaxed opacity-75 max-w-md">
                      {svc.desc}
                    </p>
                    <button
                      data-testid={`service-cta-${svc.num}`}
                      onClick={() => scrollToId("booking")}
                      className="mt-8 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.28em] font-medium border-b border-[#0B1416] pb-2 hover:text-[#0D7A7D] hover:border-[#0D7A7D] transition-colors"
                    >
                      {t.services.book_cta}
                      <ArrowUpRight size={14} />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
